import { useLayoutEffect, useRef, useState, type ChangeEvent } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { FilterChipMenuItem } from '../FilterChipMenuItem/FilterChipMenuItem';
import styles from './FilterChipMenu.module.css';

/**
 * Figma: `_Filter chip dropdown menu` (93:1577) — "Internal dropdown panel that
 * opens when a Filter Chip enters its Open state. Provides a searchable,
 * multi-select list of filter options with a selection counter and bulk clear
 * action. Used as an internal sub-component — not intended for standalone use."
 *
 * The `_` prefix marks it internal, so it stays out of the package surface.
 * The panel chrome itself — surface, border, radius, shadow and the 4px offset
 * below the trigger — comes from `Popover`, which already matches this design;
 * this component is only the contents.
 */
export type FilterChipOption = {
  value: string;
  label: string;
};

export type FilterChipMenuProps = {
  options: FilterChipOption[];
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  /** Figma `Select all` — sets or clears many options at once. */
  onToggleMany: (values: string[], checked: boolean) => void;
  onClear: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Figma's literal label on the `Select all` row. */
  selectAllLabel?: string;
  /** Force every row's hover appearance, for documentation only. */
  itemState?: 'default' | 'hover';
};

export function FilterChipMenu({
  options,
  selected,
  onToggle,
  onToggleMany,
  onClear,
  search,
  onSearchChange,
  searchPlaceholder = 'Search',
  selectAllLabel = 'Select all',
  itemState,
}: FilterChipMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);
  /* Whether the list overflows — the fade at the panel's foot shows only then. */
  const [scrolling, setScrolling] = useState(false);
  const query = search.trim().toLowerCase();
  const visible =
    query === '' ? options : options.filter((o) => o.label.toLowerCase().includes(query));

  /*
   * The overflow test the custom `ScrollBar` used to report. Figma dropped
   * `_Scroll bar` from this panel on 2026-09-21, as it did from `Select`'s, and
   * the list scrolls with the platform's own bar — so it measures itself: on
   * mount, whenever the rows change, and on resize.
   */
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => setScrolling(list.scrollHeight > list.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [visible.length]);

  /*
   * Selected options float above the divider, unselected below, each keeping the
   * order they were given. This is what the `Selected?=True` variant renders —
   * the three options above its divider are exactly the three that are checked.
   * The divider only appears when it has something on both sides, which is why
   * the `Selected?=False` variant's divider is not reproduced: it sits after the
   * third row with nothing selected, which the panel can no longer produce.
   */
  const isSelected = (value: string) => selected.includes(value);
  const above = visible.filter((o) => isSelected(o.value));
  const below = visible.filter((o) => !isSelected(o.value));

  const renderItem = (option: FilterChipOption) => (
    <FilterChipMenuItem
      key={option.value}
      label={option.label}
      checked={isSelected(option.value)}
      onChange={(checked) => onToggle(option.value, checked)}
      state={itemState}
    />
  );

  /*
   * Figma `Select all` — its own component set as of 2026-08-11,
   * `_filter chip dropdown menu item select all` (215:6667). It differs from an
   * ordinary row in exactly one property, the label's font weight, so it renders
   * through the same component with `weight="medium"`.
   *
   * It still looks like a row and sorts like one: checked rows go above the
   * divider, unchecked below, and it leads whichever group it lands in.
   *
   * That single rule produces every position the design shows. Nothing selected:
   * no divider, so it is the first row under the search field. Three of five:
   * unchecked, so it heads the lower group — immediately after the divider,
   * exactly as both `State=Open` variants draw it. Everything selected: it is
   * checked, heads the upper group, and is back under the search field.
   *
   * It acts on what is visible, so searching "tal" and hitting it selects
   * Teletalk alone rather than silently reaching options the filter is hiding.
   */
  const allVisibleSelected = visible.length > 0 && below.length === 0;
  const someVisibleSelected = above.length > 0 && below.length > 0;

  const selectAllRow = (
    <FilterChipMenuItem
      key="__select-all"
      label={selectAllLabel}
      weight="medium"
      checked={allVisibleSelected}
      /* Not in Figma — the set has no mixed variant, so this changes nothing
         visually and only makes a screen reader say "mixed". */
      indeterminate={someVisibleSelected}
      onChange={(checked) => onToggleMany(visible.map((o) => o.value), checked)}
      state={itemState}
    />
  );

  return (
    <>
      <div className={styles.header}>
        <span className={styles.count}>{selected.length} selected</span>
        {/*
          Figma binds this to `Buttons` with Hierarchy=Ghost Color, and switches
          it to State=Disabled in the variant where nothing is selected.
        */}
        <Button hierarchy="ghostColor" disabled={selected.length === 0} onClick={onClear}>
          Clear
        </Button>
      </div>

      <div className={styles.body}>
        <div className={styles.searchWrapper}>
          {/*
            Figma uses an instance of the `Select` component here, which this
            repo does not have yet. Only the one state the filter chip renders is
            reproduced — see plan.md.
          */}
          <div className={styles.search}>
            <Icon name="search" className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              type="search"
              value={search}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
            />
          </div>
        </div>

        <div className={styles.listWrapper}>
          <div
            ref={listRef}
            className={styles.list}
          >
            {/* Nothing to select all of, so the row would be a no-op control. */}
            {allVisibleSelected && selectAllRow}
            {above.map(renderItem)}
            {above.length > 0 && below.length > 0 && <div className={styles.divider} role="separator" />}
            {!allVisibleSelected && visible.length > 0 && selectAllRow}
            {below.map(renderItem)}
            {visible.length === 0 && (
              /* Not in Figma — the design has no empty state, but search can produce one. */
              <p className={styles.empty}>No matches</p>
            )}
            {/*
              Figma `Overlay` — the scrim holding the list's foot while the rows
              scroll under it. Figma hangs it off the panel, but it lives in the
              list: a native scrollbar paints above its own scroller's content
              and nothing else, so a sibling scrim washed the bar out.

              Only while the list overflows — it is a scroll affordance, and
              there is nothing to fade out on a list that fits. Decorative, so
              `aria-hidden`.
            */}
            {scrolling && <span className={styles.scrim} aria-hidden />}
          </div>
        </div>
      </div>

    </>
  );
}
